import { Line, Text } from "@react-three/drei";
import React from "react";
import { SERIF } from "../../../../constants";
import shallow from "zustand/shallow";
import { TireRackItemI } from "../../../../store/types/store";
import { useStoreRoom } from "../../../../store";
import convertDegreesToRadians from "../../../../utils/convertDegreesToRadians";
import { Mesh } from "three";
import { getGroupSize } from "./createBboxMesh";

interface TireRackDimensionsProps {
    tireRack: TireRackItemI;
    visible: boolean;
    bBoxTireRack?: Mesh;
}

// Функция для расчета расстояний до границ комнаты
const calcTireRackPaddings = (tireRack: TireRackItemI, room: any, size?: any) => {
    const roomLength = room.roomLength / 1000; // м
    const roomDepth = room.roomDepth / 1000; // м

    // Размеры стеллажа (по умолчанию или из bbox)
    const rackWidth = size?.x || 0;
    const rackDepth = size?.z || 0;

    // Позиция центра стеллажа
    const centerX = tireRack.position[0];
    const centerZ = tireRack.position[2];

    // Расстояния до краев стеллажа от его центра
    const leftEdge = centerX - rackWidth / 2;
    const rightEdge = centerX + rackWidth / 2;
    const frontEdge = centerZ - rackDepth / 2; // передний край (к Z=0)
    const backEdge = centerZ + rackDepth / 2; // задний край (к Z=roomDepth)

    // Расстояния от краев стеллажа до стен комнаты (в мм)
    // Система координат: X от -roomLength/2 до +roomLength/2, Z от 0 до roomDepth
    const left = Math.round((roomLength / 2 + leftEdge) * 1000); // расстояние от левого края до левой стены (X=-roomLength/2)
    const right = Math.round((roomLength / 2 - rightEdge) * 1000); // расстояние от правого края до правой стены (X=+roomLength/2)
    const front = Math.round(frontEdge * 1000); // расстояние от переднего края до передней стены (Z=0)
    const back = Math.round((roomDepth - backEdge) * 1000); // расстояние от заднего края до задней стены (Z=roomDepth)

    return { left, right, top: back, bottom: front };
};

const TireRackDimensions: React.FC<TireRackDimensionsProps> = (props) => {
    const { tireRack, visible, bBoxTireRack } = props;

    const room = useStoreRoom(
        (state) => ({
            roomLength: state.length,
            roomHeight: state.height,
            roomDepth: state.depth,
        }),
        shallow
    );

    if (!visible) return null;

    const bBoxSize = bBoxTireRack && getGroupSize(bBoxTireRack).size;
    const paddingsPayload = calcTireRackPaddings(tireRack, room, bBoxSize);

    // Размеры стеллажа
    const rackWidth = bBoxSize?.x || 0;
    const rackDepth = bBoxSize?.z || 0;
    const rackHeight = bBoxSize?.y || 0;

    const z = 0.1; // Фиксированная высота отображения относительно объекта
    const indent = 0.05;

    // Границы объекта в локальных координатах
    const boundingX = rackWidth / 2;
    const boundingZ = rackDepth / 2;

    // Передняя сторона (top)
    const frontStart = boundingZ + indent;
    const frontEnd = paddingsPayload.top / 1000 + boundingZ - indent;
    const frontMiddle = boundingZ + paddingsPayload.top / 2000;

    // Задняя сторона (bottom)
    const backStart = -boundingZ - indent;
    const backEnd = -(paddingsPayload.bottom / 1000 + boundingZ - indent);
    const backMiddle = -(boundingZ + paddingsPayload.bottom / 2000);

    // Левая сторона
    const leftStart = -boundingX - indent;
    const leftEnd = -(paddingsPayload.left / 1000 + boundingX - indent);
    const leftMiddle = -(boundingX + paddingsPayload.left / 2000);

    // Правая сторона
    const rightStart = boundingX + indent;
    const rightEnd = paddingsPayload.right / 1000 + boundingX - indent;
    const rightMiddle = boundingX + paddingsPayload.right / 2000;

    return (
        <group>
            {/* Передняя сторона */}
            <group position={[tireRack.position[0], tireRack.position[1], tireRack.position[2]]}>
                <Line
                    points={[
                        [-SERIF, z, frontStart],
                        [SERIF, z, frontStart],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [-SERIF, z, frontEnd],
                        [SERIF, z, frontEnd],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [0, z, frontStart],
                        [0, z, frontEnd],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text
                    color="white"
                    anchorX="center"
                    anchorY="top"
                    position={[0.15, z, frontMiddle]}
                    rotation={[convertDegreesToRadians(270), 0, 0]}
                >
                    {paddingsPayload.top}
                </Text>
            </group>

            {/* Задняя сторона */}
            <group position={[tireRack.position[0], tireRack.position[1], tireRack.position[2]]}>
                <Line
                    points={[
                        [-SERIF, z, backStart],
                        [SERIF, z, backStart],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [-SERIF, z, backEnd],
                        [SERIF, z, backEnd],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [0, z, backStart],
                        [0, z, backEnd],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text
                    color="white"
                    anchorX="center"
                    anchorY="middle"
                    position={[0.15, z, backMiddle]}
                    rotation={[convertDegreesToRadians(270), 0, 0]}
                >
                    {paddingsPayload.bottom}
                </Text>
            </group>

            {/* Левая сторона */}
            <group position={[tireRack.position[0], tireRack.position[1], tireRack.position[2]]}>
                <Line
                    points={[
                        [leftStart, z, -SERIF],
                        [leftStart, z, SERIF],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [leftEnd, z, -SERIF],
                        [leftEnd, z, SERIF],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [leftStart, z, 0],
                        [leftEnd, z, 0],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text
                    color="white"
                    anchorX="center"
                    anchorY="middle"
                    position={[leftMiddle, z, 0.15]}
                    rotation={[convertDegreesToRadians(270), 0, 0]}
                >
                    {paddingsPayload.left}
                </Text>
            </group>

            {/* Правая сторона */}
            <group position={[tireRack.position[0], tireRack.position[1], tireRack.position[2]]}>
                <Line
                    points={[
                        [rightStart, z, -SERIF],
                        [rightStart, z, SERIF],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [rightEnd, z, -SERIF],
                        [rightEnd, z, SERIF],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [rightStart, z, 0],
                        [rightEnd, z, 0],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text
                    color="white"
                    anchorX="center"
                    anchorY="middle"
                    position={[rightMiddle, z, 0.15]}
                    rotation={[convertDegreesToRadians(270), 0, 0]}
                >
                    {paddingsPayload.right}
                </Text>
            </group>
        </group>
    );
};

export default TireRackDimensions;
